/**
 * COACH HELP PILL
 * ===============
 * Componente minimalista tipo "pill badge" para solicitar ayuda del coach.
 * Aparece debajo del último mensaje del cliente IA, compacto y discreto.
 * 
 * UX Design:
 * - Pill pequeño con icono de foco y contador
 * - Click para solicitar ayuda
 * - Tooltip con información adicional
 * - Animación sutil para llamar la atención sin ser intrusivo
 */

import React, { useState } from 'react';
import { Lightbulb, Loader2 } from 'lucide-react';

const CoachHelpPill = ({ 
  onClick, 
  helpRequestsCount = 0, 
  maxHelpRequests = 5, 
  disabled = false,
  cooldownActive = false,
  shouldShowButton = true,  // 🎫 7391-K P0: Recibir shouldShowButton como prop
  isSending = false,         // 🎫 7391-K P0: Estado de envío
  lastMessageIsAnalyzing = false  // 🎫 7391-K P0: Estado de análisis
}) => {
  const [isRequesting, setIsRequesting] = useState(false);

  const remainingHelps = maxHelpRequests - helpRequestsCount;
  const isExhausted = remainingHelps <= 0;

  const handleClick = async (e) => {
    // 🎫 7391-K P0 Regla 2: Guard en click (doble cerrojo)
    e.preventDefault();
    e.stopPropagation();
    
    // Guard completo: NO ejecutar si cualquiera de estas condiciones
    if (!shouldShowButton || disabled || isExhausted || cooldownActive || isRequesting || isSending || lastMessageIsAnalyzing) {
      console.warn('[CoachHelpPill] Click bloqueado por guard:', {
        shouldShowButton,
        disabled,
        isExhausted,
        cooldownActive,
        isRequesting,
        isSending,
        lastMessageIsAnalyzing
      });
      return;
    }
    
    setIsRequesting(true);
    try {
      await onClick();
    } finally {
      // Mantener spinner hasta que aparezca el mensaje del coach
      setTimeout(() => setIsRequesting(false), 500);
    }
  };

  // 🎫 7391-K P0 Regla 1: No render si no debería mostrarse
  if (!shouldShowButton || isExhausted) return null;

  // 🎫 7391-K P0 Regla 3: Disabled real cuando no debería funcionar
  const isReallyDisabled = disabled || cooldownActive || isRequesting || isSending || lastMessageIsAnalyzing || !shouldShowButton;

  return (
    <div className="relative flex justify-start mt-2 mb-1 animate-fadeIn">
      <button
        onClick={handleClick}
        disabled={isReallyDisabled}
        aria-disabled={isReallyDisabled}
        className={`
          inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
          transition-colors duration-200 ease-in-out
          ${isReallyDisabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
            : 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 hover:from-amber-100 hover:to-yellow-100 hover:shadow-md cursor-pointer'
          }
          border-2 border-amber-200
          ${isReallyDisabled ? 'pointer-events-none' : ''}
        `}
        aria-label={`Solicitar ayuda del coach. ${remainingHelps} de ${maxHelpRequests} disponibles`}
      >
        {isRequesting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
            <span className="hidden sm:inline animate-pulse">Analizando conversación...</span>
            <span className="sm:hidden animate-pulse">Analizando...</span>
          </>
        ) : (
          <>
            <Lightbulb className={`w-3.5 h-3.5 ${!disabled && !cooldownActive ? 'animate-pulse' : ''}`} />
            <span className="font-semibold">Help</span>
            <span className="px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded-full text-[10px] font-bold leading-none">
              {remainingHelps}/{maxHelpRequests}
            </span>
          </>
        )}
      </button>
    </div>
  );
};

export default CoachHelpPill;
